import { Socket } from 'phoenix'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { debug, log } from '../log'
import { ApplianceMessage, DeviceMessage, FlagMessage, InfoMessage, SocketMessage, SystemMessage, Topic, UpdateMessage } from '../types/messaging'
import { useApp } from './useApp'
import { Safety } from '../classes/Safety'
import { VERSION } from '../version'
import { SystemControl } from '../classes/SystemControl'
import { DateTime } from 'luxon'
import { PortValuesEmSimple } from '../types/ports'
import { trySessionRefresh } from '../utils/auth'

export type SocketHandler = (msg: SocketMessage) => void

export function useSocket(topic: Topic, handler: SocketHandler) {
  const ref = useRef<Socket|null>()
  const app = useApp()
  const { t } = useTranslation()

  useEffect(() => {
    // Ignore dummy channel.
    if (topic === 'web:user:0') {
      return
    }

    log(`Connecting to ${topic}`)

    // Use a params function so each reconnection reads the current token
    // instead of reusing a stale one captured at socket creation time.
    const socket = new Socket('/socket', {
      params: () => ({ token: Safety.get('token'), version: VERSION })
    })

    socket.onError((err) => {
      console.error('WebSocket Error:', err)
    })

    socket.onOpen(() => {
      log(`Connected to ${topic}.`)
    })

    // Create the channel once. Phoenix handles reconnection and rejoin
    // automatically — the .receive('ok') callback fires on every
    // successful join/rejoin, triggering a fresh data refresh each time.
    const channel = socket.channel(topic, {})

    channel.join()
      .receive('error', (resp: unknown) => {
        if (resp === 'Unauthorized') {
          trySessionRefresh().then(newToken => {
            if (newToken) {
              log(`Token refreshed, reconnecting ${topic}`)
              // Force a socket reconnect so the server's connect/3 sees the
              // new token (params function reads it from storage).
              socket.disconnect(() => socket.connect())
            }
          })
        }
      })
      .receive('ok', () => {
        log(`Joined ${topic}.`)
        channel.push('refresh', topic)
      })

    channel.on('update', (payload: UpdateMessage) => {
      if (payload.version) {
        Safety.checkVersion(payload.version)
      }
      SystemControl.addCount(payload.hw_id, payload.port_number)
      debug('socket', topic, payload)

      // Relay tracking.
      if (payload.port_number && payload.port_number.startsWith('3/')) {
        debug('relay-tracking', `\x1b[34m${DateTime.now().toFormat('HH:mm:ss')} RELAY ${payload.hw_id} ${payload.port_number}\x1b[0m`)
        const state = 'is_on' in payload.values ? (payload.values.is_on ? 'ON' : 'OFF') : 'UNDEFINED'
        const controllable = 'is_controllable' in payload.values ? (payload.values.is_controllable ? 'CONTROLLABLE' : 'NON-CONTROLLABLE') : 'UNDEFINED'
        debug('relay-tracking', `  ${state} ${controllable}`)
      }

      // LED reader tracking.
      if (payload.port_number && payload.port_number.startsWith('101/')) {
        const values = payload.values as PortValuesEmSimple
        debug('led-tracking', `\x1b[34m${DateTime.now().toFormat('HH:mm:ss')} LED power ${values.power} energy ${values.energy}\x1b[0m`)
      }

      handler(payload)
      const event = new CustomEvent<UpdateMessage>('websocket-update', { detail: payload })
      window.dispatchEvent(event)
    })

    channel.on('device', (payload: DeviceMessage) => {
      // The device message is the first payload a client receives on join and
      // the one that can carry a port type a stale build does not know about.
      // Check the version here too (like 'update'/'system') so an outdated
      // client reloads before setDevices tries to render an unknown port.
      if (payload.version) {
        Safety.checkVersion(payload.version)
      }
      debug('socket', topic, payload)
      handler(payload)
    })

    channel.on('config', (payload: UpdateMessage) => {
      debug('socket', topic, payload)
      handler(payload)
    })

    channel.on('system', (payload: SystemMessage) => {
      debug('socket', topic, payload)
      if (payload.alert !== undefined) {
        window.scrollTo(0, 0)
        const text = t(`Alert-${payload.alert}`) !== `Alert-${payload.alert}` ? t(`Alert-${payload.alert}`) : payload.alert
        app.setAlert(text)
      }
      if (payload.version) {
        Safety.checkVersion(payload.version)
      }
    })

    channel.on('flags', (payload: FlagMessage) => {
      debug('socket', topic, payload)
      handler(payload)
    })

    channel.on('message', (payload: InfoMessage) => {
      debug('socket', topic, payload)
      switch (payload.type) {
        case 'info':
          app.addInfo(t(payload.text))
          break
        case 'warning':
          app.addWarning(t(payload.text))
          break
        default:
          app.addError(t(payload.text))
          break
      }
    })

    channel.on('appliance', (payload: ApplianceMessage) => {
      debug('appliance', topic, payload)
      handler(payload)
    })

    socket.connect()
    ref.current = socket

    return () => {
      log(`Disconnecting from ${topic}`)
      channel.leave()
      socket.disconnect()
      ref.current = undefined
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic])
}
